/**
 * OWA-1433: an open task editor's draft follows the same user's own saves of that task.
 *
 * The editor keeps a draft and the accepted version it began from (its base). When the user saves
 * the same task somewhere else (the invoice's EX or NC checkbox, a time or notes cell), the newer
 * version used to leave the draft on its old base, so every later save from the editor was refused
 * as stale and the user was asked to merge against themself.
 *
 * A draft is rebased onto a newer version only when that version is an accepted one saved by the
 * same user and no field was changed both by the draft and by the newer version to different
 * values. The rebased draft is the newer version with the draft's own changes applied, and names
 * the newer version as its base. Anything else leaves the draft alone, so its save is refused and
 * the user resolves it (OWA-998). The server's stale check is unchanged.
 */

import type Task from '../entities/task';
import {changedContentFields, sameVersion, versionRef} from './staleSave';
import {acceptedVersion} from './taskLineage';

/**
 * Fields updateCost computes from other fields (time, overrides, rates and flags). Both sides recompute them
 * at different moments, so a difference is not a conflict; the editor recomputes them on the rebased draft.
 */
const DERIVED = new Set(['cost', 'laborTime', 'travelTime']);

/**
 * A copy of a version that shares no object with it. The editor edits some fields in place
 * (notification, for one), and a recorded base that shared them with the draft would change with
 * the draft and hide those edits from the merge. Null when the version cannot be copied.
 *
 * @param {Task} task
 * @returns {Task | null}
 */
export function snapshotVersion(task: Task): Task | null {
    try {
        return new (task.constructor as new (doc: unknown) => Task)(structuredClone({...task}));
    }
    catch (e) {
        console.warn(`OWA-1433: task ${task.id} could not be copied as a draft's base`, e);
        return null;
    }
}

/**
 * The draft rebased onto `incoming`, or null when it must keep its base.
 *
 * @param {Task} draft - the editor's working copy
 * @param {Task} base - the version the draft was made from
 * @param {Task} incoming - the newer version in the store
 * @param {string} userId - the signed-in user
 * @returns {Task | null}
 */
export function rebaseOwnDraft(draft: Task, base: Task, incoming: Task, userId: string): Task | null {
    if (incoming.addedBy !== userId) return null;
    // Only an accepted version: not this tab's queued copy, not a deletion
    if (incoming.pendingOp || !incoming.dbAddedOn || incoming.status === 'D') return null;
    if (draft.id !== incoming.id || base.id !== incoming.id) return null;
    if (Number(incoming.dbAddedOn) <= Number(base.dbAddedOn ?? 0)) return null;
    // A base that is this tab's unacknowledged save is followed only to that save's own acceptance:
    // against any other version its changes would read as the newer version undoing them
    if (base.pendingOp) {
        const accepted = acceptedVersion(base.pendingOp);
        if (!accepted || !sameVersion(incoming as Task & {addedOn: number}, accepted)) return null;
    }

    const theirs = new Set(changedContentFields(base, incoming));
    const mine = changedContentFields(base, draft);
    const differ = new Set(changedContentFields(draft, incoming));
    if (mine.some(f => theirs.has(f) && differ.has(f) && !DERIVED.has(f))) return null;

    const rebased = incoming.clone();
    const target = rebased as unknown as Record<string, unknown>;
    const source = draft as unknown as Record<string, unknown>;
    // A derived field both sides changed keeps the newer version's value until the editor recomputes it
    for (const f of mine) if (!(DERIVED.has(f) && theirs.has(f))) target[f] = source[f];
    rebased.baseVersion = versionRef(incoming as Task & {addedOn: number}) as Task['baseVersion'];
    rebased.pendingOp = null;
    return rebased;
}
